import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router';

import { useSetLang } from '../components/app-shell.js';
import { LanguageToggle } from '../components/language-toggle.js';
import { trackEvent } from '../lib/analytics.js';
import { FALLBACK_EN_CONTENT, fetchDiscoveryContent } from '../lib/cms-client.js';
import type { DiscoveryPageLocaleContent } from '../lib/cms-client.js';

import styles from './discovery/styles.module.css';

interface DiscoveryPageProps {
  lang?: 'en' | 'es';
}

const CHECK_PATH = 'M20 6 9 17l-5-5';

function Check({ strokeWidth = 2.4 }: { strokeWidth?: number }): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" strokeWidth={strokeWidth} aria-hidden="true">
      <path d={CHECK_PATH} />
    </svg>
  );
}

/** Phone mock framing a real Betty app screen (assets from the design prototype). */
function PhoneMock({ small = false }: { small?: boolean }): JSX.Element {
  return (
    <div className={styles.phoneWrap} aria-hidden="true">
      <div className={`${styles.phone}${small ? ` ${styles.phoneSm}` : ''}`}>
        <div className={styles.screen}>
          <img
            className={styles.screenImg}
            src={small ? '/images/hub-chat.webp' : '/images/hub-app.webp'}
            alt=""
          />
        </div>
      </div>
      {!small && (
        <>
          <div className={`${styles.pin} ${styles.pinOne}`}>
            <span className={styles.pinEm}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--coral-dark)"
                strokeWidth="2"
                style={{ width: 20, height: 20 }}
              >
                <path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.5-7 10-7 10Z" />
              </svg>
            </span>
            <span>
              Daily check-in
              <small>takes 10 seconds</small>
            </span>
          </div>
          <div className={`${styles.pin} ${styles.pinTwo}`}>
            <span className={styles.pinEm}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#5a8a2e"
                strokeWidth="2"
                style={{ width: 20, height: 20 }}
              >
                <path d="M12 22V12M12 12c0-3 2-5 5-5 0 3-2 5-5 5Zm0 0C12 9 10 7 7 7c0 3 2 5 5 5Z" />
              </svg>
            </span>
            <span>
              A1c improved
              <small>goal reached 🎉</small>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const FEATURES: Array<{ icon: JSX.Element; title: string; body: string }> = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
      </svg>
    ),
    title: 'Daily check-ins',
    body: 'A friendly hello each day — mood, meals, energy. Ten seconds, and Betty replies. Miss a day? No guilt, ever.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
        <path d="M3 12h4l2 5 4-12 2 7h6" />
      </svg>
    ),
    title: 'Meal logging & tips',
    body: 'Tell Betty what you ate and get advice specific to your condition — plus a recipe you might like next.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
        <path d="M4 19V5a2 2 0 0 1 2-2h10l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
        <path d="M8 9h6M8 13h8" />
      </svg>
    ),
    title: 'Learn at your pace',
    body: 'Short lessons as video, audio, or articles — plus quizzes. Matched to your goals, in English or Spanish.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 21h8M9 11l2 2 4-4" />
      </svg>
    ),
    title: 'Recipes & prepared meals',
    body: "Get recipes for what's already in your fridge, or order clinically-appropriate prepared meals when it's easier.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
        <circle cx="9" cy="8" r="3" />
        <circle cx="17" cy="9" r="2.2" />
        <path d="M3 19c0-3.5 3-5.5 6-5.5s6 2 6 5.5M15 18c0-2 1.4-3.4 3.4-3.4S22 16 22 18" />
      </svg>
    ),
    title: 'Group sessions with an RD',
    body: 'Book live group consults led by a Registered Dietitian on the topics you care about. Reminders included.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden="true">
        <path d="M12 22V12M12 12c0-3 2-5 5-5 0 3-2 5-5 5Zm0 0C12 9 10 7 7 7c0 3 2 5 5 5Z" />
      </svg>
    ),
    title: 'Real, achievable goals',
    body: 'Set goals that matter to you, track milestones, and see Betty connect each win to real health progress.',
  },
];

const TIMELINE: Array<{ when: string; title: string; body: string }> = [
  {
    when: 'Before you start',
    title: 'Your doctor refers you',
    body: 'A provider submits a referral and we confirm your Medicare eligibility.',
  },
  {
    when: 'Week 1',
    title: 'Enroll & meet Betty',
    body: 'Quick consent and survey online, then download the app and Betty says hello.',
  },
  {
    when: 'Months 1–3',
    title: 'Build daily habits',
    body: 'Check-ins, meal tips, lessons, and your first milestones along the way.',
  },
  {
    when: 'Months 3–9',
    title: 'See real results',
    body: 'Quarterly labs, RD group sessions, and health numbers trending the right way.',
  },
  {
    when: 'Month 12',
    title: 'Graduate',
    body: 'Goals met, a year-in-review, and an outcomes summary for your doctor.',
  },
];

const SHARE_PREVIEW_MESSAGE =
  "Hi, I'm interested in joining the NourishedRx nutrition program through Medicare. " +
  "Here's a link to submit a referral for me:";

/**
 * DiscoveryPage — `/` route (CMS-driven member-facing landing).
 *
 * Layout and copy follow the NourishedRx ACCESS member-landing design prototype.
 * CMS-driven slots: program overview, benefits checklist, supported-condition chips,
 * enrollment steps, FAQ items, provider directory link.
 *
 * Analytics: discovery_page_viewed on mount, provider_link_shared on share/copy,
 * provider_directory_clicked on directory link click.
 *
 * No PII collected. No auth required. No backend writes from this page.
 */
export function DiscoveryPage({ lang = 'en' }: DiscoveryPageProps): JSX.Element {
  const [content, setContent] = useState<DiscoveryPageLocaleContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const setLang = useSetLang();

  useEffect(() => {
    trackEvent('discovery_page_viewed', { source: 'access_landing' });

    void fetchDiscoveryContent(lang)
      .then((data) => {
        setContent(data);
        setIsLoading(false);
      })
      .catch(() => {
        // fetchDiscoveryContent handles fallback internally — if it throws, use static fallback
        setContent(FALLBACK_EN_CONTENT);
        setIsLoading(false);
      });
  }, [lang]);

  // react-router does not scroll to hash anchors on load — handle #section links
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      document.querySelector(hash)?.scrollIntoView();
    }
  }, []);

  // Browser-only SPA — window is always defined at runtime (jsdom in tests).
  const shareUrl = `${window.location.origin}/refer?ref=member_share`;
  const shareMessage = `${SHARE_PREVIEW_MESSAGE} ${shareUrl}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    'Please refer me to NourishedRx',
  )}&body=${encodeURIComponent(shareMessage)}`;

  function showCopied(): void {
    setCopied(true);
    setTimeout(() => setCopied(false), 4000);
  }

  async function handleCopy(): Promise<void> {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackEvent('provider_link_shared', { source: 'member_discovery_page' });
      showCopied();
    } catch {
      // Clipboard write failed — no analytics event; silent fallback
    }
  }

  async function handleShare(): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'NourishedRx ACCESS', text: shareMessage, url: shareUrl });
        // Only fire analytics on successful share — not on user-cancel (AbortError)
        trackEvent('provider_link_shared', { source: 'member_discovery_page' });
      } catch (err) {
        // User cancelled the share sheet (DOMException: AbortError) — do NOT fire analytics
        if (err instanceof Error && err.name !== 'AbortError') {
          console.warn('[discovery-page] navigator.share failed:', err.message);
        }
      }
    } else if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        trackEvent('provider_link_shared', { source: 'member_discovery_page' });
        showCopied();
      } catch {
        // Clipboard write failed — no analytics event; show no error UI (silent fallback)
      }
    } else {
      console.warn('[discovery-page] Neither navigator.share nor navigator.clipboard is available');
    }
  }

  function handleProviderDirectoryClick(): void {
    trackEvent('provider_directory_clicked', { source: 'member_discovery_page' });
  }

  const displayContent = content ?? FALLBACK_EN_CONTENT;

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarRow}>
          <Link to="/" className={styles.logo} aria-label="NourishedRx home">
            <img src="/logo.png" alt="NourishedRx" />
          </Link>
          <div className={styles.topbarActions}>
            <LanguageToggle lang={lang} onLangChange={setLang} />
            <Link to="/provider" className={styles.forProviders}>
              For Providers
            </Link>
            <a href="#choose" className={`${styles.btn} ${styles.btnPrimary}`}>
              Get started
            </a>
          </div>
        </div>
      </header>
      <nav className={styles.navbar} aria-label="Section navigation">
        <div className={styles.navbarRow}>
          <a href="#what">About the Program</a>
          <a href="#betty">The Betty Experience</a>
          <a href="#conditions">Who It&apos;s For</a>
          <a href="#year">Your Year</a>
          <a href="#provider">For Providers</a>
          <a href="#beneficiary">For Members</a>
        </div>
      </nav>

      <main>
        {/* HERO */}
        <section className={styles.hero}>
          <div className={styles.wrap}>
            <div className={styles.heroGrid}>
              <div>
                <span className={styles.eyebrow}>Medicare ACCESS Program</span>
                <h1>
                  A digital nutrition program,{' '}
                  <span className={styles.coral}>covered by Medicare.</span>
                </h1>
                <p className={styles.heroLede}>
                  Meet <strong>Betty</strong> — your personal health companion. Daily support,
                  nutrition guidance, recipes, and real results, all from your phone.{' '}
                  <strong>No cost to eligible members.</strong>
                </p>
                <div className={styles.badges}>
                  <span className={styles.badgeChip}>
                    <Check strokeWidth={2.2} /> 100% digital program
                  </span>
                  <span className={styles.badgeChip}>
                    <Check strokeWidth={2.2} /> A real registered dietitian
                  </span>
                  <span className={styles.badgeChip}>
                    <Check strokeWidth={2.2} /> English &amp; Spanish
                  </span>
                </div>
                <a href="#choose" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}>
                  How to get started ↓
                </a>
              </div>
              <PhoneMock />
            </div>
          </div>
        </section>

        {/* WHAT IS IT */}
        <section className={styles.secPad} id="what">
          <div className={styles.wrap}>
            <div className={styles.whatisTwo}>
              <div>
                <span className={styles.eyebrow}>About the program</span>
                <h2 className={styles.whatisHeading}>
                  Real support for your health — right on your phone.
                </h2>
                {isLoading ? (
                  <p className={styles.whatisPara}>Loading program information…</p>
                ) : (
                  <p className={styles.whatisPara}>{displayContent.programOverview}</p>
                )}
                {!isLoading && displayContent.benefitsList.length > 0 && (
                  <ul className={styles.benefitList}>
                    {displayContent.benefitsList.map((benefit) => (
                      <li key={benefit}>
                        <span className={styles.benefitCk}>
                          <Check />
                        </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                )}
                <div className={styles.nofood}>
                  <h4>A whole digital experience</h4>
                  <p>
                    Nutrition education, personalized recipes, and the option to order
                    clinically-appropriate prepared meals when you want them — all guided by Betty.
                  </p>
                </div>
              </div>
              <PhoneMock small />
            </div>
          </div>
        </section>

        {/* BETTY FEATURES */}
        <section className={`${styles.secPad} ${styles.features}`} id="betty">
          <div className={`${styles.wrap} ${styles.center}`}>
            <span className={styles.eyebrow}>The Betty experience</span>
            <h2 className={styles.sectionH2}>Everything in one friendly app</h2>
            <p className={styles.condIntro}>
              Betty feels like a health companion, not an app — designed around your real life.
            </p>
          </div>
          <div className={styles.wrap}>
            <div className={styles.featGrid}>
              {FEATURES.map((feat) => (
                <div key={feat.title} className={styles.feat}>
                  <div className={styles.featIc}>{feat.icon}</div>
                  <h4>{feat.title}</h4>
                  <p>{feat.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MILESTONES & GOALS */}
        <section className={`${styles.secPad} ${styles.garden}`}>
          <div className={styles.wrap}>
            <div className={styles.gardenInner}>
              <div>
                <span className={`${styles.eyebrow} ${styles.eyebrowLight}`}>
                  Milestones &amp; goals
                </span>
                <h2>Motivation you can feel. Outcomes you can measure.</h2>
                <p>
                  The heart of the experience is progress. Members set goals that matter to them,
                  hit milestones along the way, and watch Betty connect each one to a real health
                  win — like better blood pressure or a lower A1c.
                </p>
                <p className={styles.gardenQuote}>
                  &ldquo;Seeing my numbers move — and Betty cheering me on — is what kept me going
                  all year.&rdquo;
                </p>
              </div>
              <div>
                <div className={styles.mstack} aria-hidden="true">
                  <div className={styles.mcard}>
                    <span className={styles.mic} style={{ background: '#eaf6e0' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#5a8a2e" strokeWidth="2">
                        <path d={CHECK_PATH} />
                      </svg>
                    </span>
                    <span>
                      <b>14-day check-in streak</b>
                      <small>Milestone reached</small>
                    </span>
                    <span className={styles.done}>
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="3">
                        <path d={CHECK_PATH} />
                      </svg>
                    </span>
                  </div>
                  <div className={styles.mcard}>
                    <span className={styles.mic} style={{ background: '#e6f3f0' }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#4095a5" strokeWidth="2">
                        <path d="M4 19V5a2 2 0 0 1 2-2h10l4 4v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
                        <path d="M8 9h6M8 13h8" />
                      </svg>
                    </span>
                    <span>
                      <b>First 5 lessons complete</b>
                      <small>Milestone reached</small>
                    </span>
                    <span className={styles.done}>
                      <svg viewBox="0 0 24 24" fill="none" strokeWidth="3">
                        <path d={CHECK_PATH} />
                      </svg>
                    </span>
                  </div>
                  <div className={`${styles.mcard} ${styles.mcardGoal}`}>
                    <span className={styles.mic}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="#ff9a90" strokeWidth="2">
                        <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
                      </svg>
                    </span>
                    <span>
                      <b>My goal: walk after dinner, 5×/week</b>
                      <small>You set this one</small>
                    </span>
                    <span className={styles.pct}>4/5</span>
                  </div>
                </div>
                <div className={styles.goalbox}>
                  <span className={styles.goalLabel}>Progress toward your goal</span>
                  <div className={styles.goalBar}>
                    <span className={styles.goalBarFill} />
                  </div>
                  <p className={styles.goalNote}>
                    You&apos;re 68% of the way to your 12-month goal. Keep going — Betty&apos;s got
                    you.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONDITIONS */}
        <section className={`${styles.secPad} ${styles.center}`} id="conditions">
          <div className={styles.wrap}>
            <span className={styles.eyebrow}>Who it&apos;s for</span>
            <h2 className={styles.sectionH2}>
              Built for Medicare members managing chronic conditions
            </h2>
            <p className={styles.condIntro}>
              NourishedRx supports the cardiometabolic conditions tracked by the ACCESS program (CKM
              &amp; eCKM tracks).
            </p>
            {!isLoading && displayContent.supportedConditions.length > 0 && (
              <ul className={styles.condRow} style={{ listStyle: 'none', padding: 0 }}>
                {displayContent.supportedConditions.map((condition) => (
                  <li key={condition} className={styles.condChip}>
                    {condition}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* YEAR TIMELINE */}
        <section className={`${styles.secPad} ${styles.yearSection}`} id="year">
          <div className={`${styles.wrap} ${styles.center}`}>
            <span className={styles.eyebrow}>Your year, step by step</span>
            <h2 className={styles.sectionH2}>A full 12 months of support</h2>
          </div>
          <div className={styles.wrap}>
            <div className={styles.timeline}>
              {TIMELINE.map((step, idx) => (
                <div key={step.title} className={styles.tl}>
                  <div className={styles.tlDot}>{idx + 1}</div>
                  <div>
                    <span className={styles.tlWhen}>{step.when}</span>
                    <h4>{step.title}</h4>
                    <p>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AUDIENCE QUICK CHOOSER */}
        <section className={`${styles.secPad} ${styles.bottomsplit}`} id="choose">
          <div className={`${styles.wrap} ${styles.center}`}>
            <h2 className={styles.sectionH2} style={{ marginTop: 0 }}>
              How can we help you today?
            </h2>
            <p className={styles.chooserIntro}>
              Enrollment always starts with a provider referral — members can&apos;t self-enroll.
              Pick the path that fits you.
            </p>
            <div className={styles.chooserCards}>
              <Link to="/provider" className={`${styles.choose} ${styles.chooseProv}`}>
                <span className={styles.chooseIc}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#01261f" strokeWidth="2">
                    <path d="M8 21h8M12 17v4M5 3h14a1 1 0 0 1 1 1v9a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V4a1 1 0 0 1 1-1Z" />
                    <path d="M12 7v4M10 9h4" />
                  </svg>
                </span>
                <span>
                  <b>I&apos;m a provider</b>
                  <small>Refer a patient &amp; see care coordination</small>
                </span>
                <span className={styles.chooseGo} aria-hidden="true">
                  →
                </span>
              </Link>
              <a href="#beneficiary" className={`${styles.choose} ${styles.chooseMem}`}>
                <span className={styles.chooseIc}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#d83a32" strokeWidth="2">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                  </svg>
                </span>
                <span>
                  <b>I&apos;m a Medicare beneficiary</b>
                  <small>Learn how to ask your doctor to refer you</small>
                </span>
                <span className={styles.chooseGo} aria-hidden="true">
                  ↓
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* FOR PROVIDERS */}
        <section className={styles.secPad} id="provider">
          <div className={styles.wrap}>
            <div className={styles.provInner}>
              <div>
                <span className={styles.eyebrow}>For healthcare providers</span>
                <h2 className={styles.provHeading}>Refer a patient in about five minutes.</h2>
                <p className={styles.provPara}>
                  Submit referrals straight from the form — no login. We verify eligibility with
                  CMS, enroll your patient, and reach out to you with next steps for care
                  coordination.
                </p>
                <ul className={styles.provPoints}>
                  <li>
                    <span className={styles.provCk}>
                      <Check />
                    </span>
                    No login — the form is public and takes ~5 minutes
                  </li>
                  <li>
                    <span className={styles.provCk}>
                      <Check />
                    </span>
                    Stay informed: labs, milestones, and a year-end outcomes summary
                  </li>
                  <li>
                    <span className={styles.provCk}>
                      <Check />
                    </span>
                    CMS compliance supported — we track labs and compile outcome data
                  </li>
                </ul>
                <div className={styles.provCta}>
                  <Link
                    to="/refer"
                    className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}
                  >
                    Open the referral form →
                  </Link>
                  <Link to="/provider" className={`${styles.btn} ${styles.btnGhost}`}>
                    How referrals work
                  </Link>
                </div>
                <p className={styles.provDirectory}>
                  {displayContent.providerDirectoryUrl ? (
                    <a
                      href={displayContent.providerDirectoryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleProviderDirectoryClick}
                    >
                      Find a participating provider
                    </a>
                  ) : (
                    <>
                      Looking for a participating provider? Email{' '}
                      <a href="mailto:access@nourishedrx.com">access@nourishedrx.com</a>.
                    </>
                  )}
                </p>
              </div>
              <div className={styles.provCard}>
                <div className={styles.stepMini}>
                  <span className={styles.stepMiniNum}>1</span>
                  <div>
                    <b>Submit the referral</b>
                    <p>Patient details, condition, and a few clinical readings. About 5 minutes.</p>
                  </div>
                </div>
                <div className={styles.stepMini}>
                  <span className={styles.stepMiniNum}>2</span>
                  <div>
                    <b>We verify &amp; enroll</b>
                    <p>CMS eligibility check fires; your patient is contacted in your name.</p>
                  </div>
                </div>
                <div className={styles.stepMini}>
                  <span className={styles.stepMiniNum}>3</span>
                  <div>
                    <b>We coordinate care</b>
                    <p>If they enroll, we reach out with care-coordination next steps.</p>
                  </div>
                </div>
                <div className={styles.stepMini}>
                  <span className={styles.stepMiniNum}>4</span>
                  <div>
                    <b>You stay informed</b>
                    <p>Updates flow back to you for the full 12 months.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOR MEDICARE BENEFICIARIES */}
        <section className={`${styles.secPad} ${styles.bene}`} id="beneficiary">
          <div className={`${styles.wrap} ${styles.center}`}>
            <span className={`${styles.eyebrow} ${styles.eyebrowLight}`}>
              For Medicare beneficiaries
            </span>
            <h2>Ask your doctor to refer you</h2>
            <p className={styles.beneLead}>
              You can&apos;t sign yourself up — Medicare requires that a provider refers you. The
              good news: it only takes your doctor a few minutes, and you can send them everything
              they need right now.
            </p>
            {!isLoading && displayContent.enrollmentSteps.length > 0 && (
              <div className={styles.beneSteps}>
                {displayContent.enrollmentSteps.map((step) => (
                  <div key={step.stepNumber} className={styles.beneStep}>
                    <div className={styles.beneStepNum} aria-hidden="true">
                      {step.stepNumber}
                    </div>
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                ))}
              </div>
            )}
            <div className={styles.cardShare}>
              <p className={styles.cardShareTitle}>Send your doctor your referral link</p>
              <div className={styles.linkbox}>
                <input readOnly value={shareUrl} aria-label="Your referral link" />
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnGhost}`}
                  onClick={() => void handleCopy()}
                >
                  Copy
                </button>
              </div>
              <div className={styles.shareActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}
                  onClick={() => void handleShare()}
                >
                  Share with my provider
                </button>
                <a href={emailHref} className={`${styles.btn} ${styles.btnGhost}`}>
                  Email it
                </a>
              </div>
              <p
                className={`${styles.copied}${copied ? ` ${styles.copiedShow}` : ''}`}
                aria-live="polite"
              >
                ✓ Copied! Paste it into a text or email to your provider.
              </p>
              <div className={styles.previewMsg}>
                <b>Message we&apos;ll send</b>
                <span>{SHARE_PREVIEW_MESSAGE}</span>
              </div>
            </div>
            <p className={styles.beneContact}>
              Have a question first? Email{' '}
              <a href="mailto:access@nourishedrx.com">
                <b>access@nourishedrx.com</b>
              </a>{' '}
              — a real person will help.
            </p>
          </div>
        </section>

        {/* FAQ */}
        {!isLoading && displayContent.faqItems.length > 0 && (
          <section className={styles.secPad} id="faqs">
            <div className={styles.wrapNarrow}>
              <h2 className={`${styles.center} ${styles.sectionH2}`} style={{ marginTop: 0 }}>
                Frequently Asked Questions
              </h2>
              <div className={styles.faqList}>
                {displayContent.faqItems.map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div key={faq.question} className={styles.faqItem}>
                      <button
                        type="button"
                        className={styles.faqQ}
                        aria-expanded={isOpen}
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                      >
                        <span
                          className={`${styles.faqPm}${isOpen ? ` ${styles.faqPmOpen}` : ''}`}
                          aria-hidden="true"
                        >
                          +
                        </span>
                        {faq.question}
                      </button>
                      <div className={styles.faqBody} style={{ maxHeight: isOpen ? '600px' : '0' }}>
                        <div>{faq.answer}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* FOOTER */}
      <footer className={styles.footer}>
        <div className={styles.wrap}>
          <div className={styles.footerCols}>
            <div>
              <Link to="/" className={`${styles.logo} ${styles.footerLogo}`}>
                <img src="/logo.png" alt="NourishedRx" />
              </Link>
              <p className={styles.footerAbout}>
                A digital nutrition program for Medicare beneficiaries, delivered through the ACCESS
                program.
              </p>
              <p className={styles.footerContact}>
                Questions?{' '}
                <a href={`mailto:${displayContent.contactEmail}`}>{displayContent.contactEmail}</a>
              </p>
            </div>
            <div>
              <h5>For Providers</h5>
              <ul>
                <li>
                  <Link to="/provider">How referrals work</Link>
                </li>
                <li>
                  <Link to="/provider#coordination">Care coordination</Link>
                </li>
                <li>
                  <Link to="/refer">Refer a patient</Link>
                </li>
              </ul>
            </div>
            <div>
              <h5>For Members</h5>
              <ul>
                <li>
                  <a href="#beneficiary">How to enroll</a>
                </li>
                <li>
                  <a href="#beneficiary">Tell your provider</a>
                </li>
                <li>
                  <a href="#what">What you get</a>
                </li>
              </ul>
            </div>
            <div>
              <h5>Program</h5>
              <ul>
                <li>
                  <a href="#what">Overview</a>
                </li>
                <li>
                  <a href="#faqs">FAQs</a>
                </li>
                <li>
                  <a href={`mailto:${displayContent.contactEmail}`}>Contact Us</a>
                </li>
              </ul>
            </div>
          </div>
          <div className={styles.footerLegal}>
            <span>NourishedRx © 2026 · All Rights Reserved.</span>
            <span className={styles.legalFaint}>
              A covered benefit administered through the Medicare ACCESS program.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DiscoveryPage;
